import classnames from "classnames";
import { hex2rgba, shadeColor } from "modules/common/services/colors";
import React, { useCallback, useMemo } from "react";
import { blocksOf } from "../models/plan-queue";
import { columnLeft, columnsWidth, PlanGrid } from "../models/plan-scale";
import { CellSelection, isWork, PlanColumn, PlanProject, PlanRow } from "../models/plan-types";
import { absenceKind, absenceShortName, PlanAbsenceKind } from "./plan-absences";
import {
    BLOCK_CLASS,
    columnAt,
    HANDLE_LEFT_CLASS,
    HANDLE_RIGHT_CLASS,
    PAST_BLOCK_CLASS,
    SELECTION_CLASS,
    TRACK_CLASS,
} from "./plan-geometry";
import styles from "./PlanRowView.module.scss";

/** На сколько затемнить цвет проекта для рамки блока */
const BORDER_SHADE = -30;

/**
 * Уже этой ширины подпись в блок не влезает — в недельном масштабе таких много.
 * Порог держится ниже ширины дня (30px): однодневный блок обязан показывать номер,
 * иначе самый частый случай — «залил один день» — остаётся без подписи.
 */
const LABEL_MIN_WIDTH = 26;

/** Ниже этой ширины подпись рисуется компактно: мельче, без отступов, по центру */
const LABEL_TIGHT_WIDTH = 44;

const FALLBACK_COLOR = "#9b9b9b";

/** Уже этой ширины дня буква отсутствия не читается — оставляем чистую заливку */
const ABSENCE_LETTER_MIN_WIDTH = 9;

/** Прозрачность заливки блока — та же, что уходит в hex2rgba */
const BLOCK_ALPHA = 0.75;

/** Ниже этой яркости подложки тёмная подпись сливается с цветом проекта */
const LIGHT_TEXT_THRESHOLD = 150;

/** shadeColor работает только с полным hex — подстраховываемся от пустых значений */
function normalizeColor(color: string): string {
    return /^#[0-9a-f]{6}$/i.test(color) ? color : FALLBACK_COLOR;
}

/**
 * Тёмная ли заливка блока. Считается по цвету, УЖЕ смешанному с белым фоном сетки:
 * блок полупрозрачный, и сам по себе тон проекта здесь ни о чём не говорит.
 */
function isDarkBlock(color: string): boolean {
    const value = parseInt(color.slice(1), 16);
    const mix = (channel: number) => channel * BLOCK_ALPHA + 255 * (1 - BLOCK_ALPHA);

    const r = mix((value >> 16) & 255);
    const g = mix((value >> 8) & 255);
    const b = mix(value & 255);

    return 0.299 * r + 0.587 * g + 0.114 * b < LIGHT_TEXT_THRESHOLD;
}

/** Цвет блока отсутствия: палитра planr, красный/синий/зелёный по группе */
const ABSENCE_CLASS: Record<PlanAbsenceKind, string> = {
    withoutPay: styles.absenceWithoutPay,
    vacation: styles.absenceVacation,
    illness: styles.absenceIllness,
    pregnancy: styles.absencePregnancy,
    other: styles.absenceOther,
};

interface PlanRowViewProps {
    row: PlanRow;
    rowIndex: number;
    columns: PlanColumn[];
    projects: Map<string, PlanProject>;
    selection: CellSelection | null;
    /** Куда встанет перетаскиваемый отрезок; null, если он не над этой строкой */
    dropPreview: CellSelection | null;
    /** Раскладка сетки: офсеты колонок и слоты */
    grid: PlanGrid;
    /** Слот (день или неделя), выделенный кликом по шапке */
    selectedSlot: { start: number; length: number } | null;
    /** Левые края разделителей месяцев, px */
    monthBreaks: number[];
    onBlockClick: (rowIndex: number, start: number, length: number) => void;
    /** Двойной клик по любому дню строки — открыть выбор проекта */
    onDayActivate: (rowIndex: number, column: number, x: number, y: number) => void;
    /** Правый клик по дню — контекстное меню */
    onDayContext: (rowIndex: number, column: number, x: number, y: number) => void;
}

export const PlanRowView: React.FC<PlanRowViewProps> = React.memo((props) => {
    const {
        row,
        rowIndex,
        columns,
        projects,
        selection,
        dropPreview,
        grid,
        selectedSlot,
        monthBreaks,
        onBlockClick,
        onDayActivate,
        onDayContext,
    } = props;

    const onDoubleClick = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            const track = event.currentTarget;
            const column = columnAt(track, event.clientX, grid);
            const rect = track.getBoundingClientRect();

            onDayActivate(rowIndex, column, event.clientX, rect.bottom + 4);
        },
        [grid, rowIndex, onDayActivate]
    );

    const onContextMenu = useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            event.preventDefault();

            const column = columnAt(event.currentTarget, event.clientX, grid);
            onDayContext(rowIndex, column, event.clientX, event.clientY);
        },
        [grid, rowIndex, onDayContext]
    );

    const blocks = useMemo(() => blocksOf(row, columns), [row, columns]);

    /** Прошлое приглушается до начала текущего слота — неделя целиком не «прошлая» */
    const pastWidth = useMemo(() => {
        const first = grid.slots.find((slot) => !slot.isPast);
        return first ? first.left : grid.totalWidth;
    }, [grid]);

    /** Сегодняшний день, а в недельном масштабе — вся текущая неделя */
    const today = useMemo(() => grid.slots.find((slot) => slot.isToday) || null, [grid]);

    /**
     * Выделение, совпадающее с блоком целиком, не должно перехватывать мышь:
     * иначе под ним недоступны ни тело блока, ни ручки растягивания. Оно тогда
     * рисуется просто рамкой, а жесты достаются самому блоку.
     */
    const selectionIsBlock = useMemo(() => {
        if (!selection || selection.rowIndex !== rowIndex) {
            return false;
        }

        return blocks.some(
            (block) => block.start === selection.start && block.length === selection.length
        );
    }, [selection, rowIndex, blocks]);

    // под местом приземления уже есть работы — предупреждаем, что они заменятся
    const overwrites = useMemo(() => {
        if (!dropPreview) {
            return false;
        }

        for (let i = dropPreview.start; i < dropPreview.start + dropPreview.length; i++) {
            if (isWork(row.cells[i])) {
                return true;
            }
        }

        return false;
    }, [dropPreview, row.cells]);

    return (
        <div className={styles.row}>
            <div className={styles.name} title={`${row.label} — ${row.position}`}>
                <div className={styles.label}>{row.label}</div>
                <div className={styles.position}>{row.position}</div>
            </div>

            <div
                className={`${TRACK_CLASS} ${styles.track}`}
                data-row={rowIndex}
                style={
                    {
                        width: grid.totalWidth,
                        // полоски фона идут по границам слотов — как колонки в шапке
                        "--wp-grid-step": `${grid.slotWidth}px`,
                    } as React.CSSProperties
                }
                onDoubleClick={onDoubleClick}
                onContextMenu={onContextMenu}
            >
                {pastWidth > 0 && <div className={styles.past} style={{ width: pastWidth }} />}

                {today && <div className={styles.today} style={{ left: today.left, width: today.width }} />}

                {selectedSlot && (
                    <div
                        className={styles.columnMark}
                        style={{
                            left: columnLeft(grid, selectedSlot.start),
                            width: columnsWidth(grid, selectedSlot.start, selectedSlot.length),
                        }}
                    />
                )}

                {/* неделя без рабочих дней: ставить в неё нечего */}
                {grid.slots.map((slot) =>
                    slot.isEmpty ? (
                        <div
                            key={`empty_${slot.left}`}
                            className={styles.emptyWeek}
                            style={{ left: slot.left, width: slot.width }}
                            title="Рабочих дней на этой неделе нет"
                        />
                    ) : null
                )}

                {monthBreaks.map((left) => (
                    <div key={left} className={styles.monthBreak} style={{ left }} />
                ))}

                {blocks.map((block) => {
                    const absence = block.absence;
                    const absenceLabel = absence ? absenceShortName(absence) : "";
                    const project = absence ? undefined : projects.get(block.orderId);
                    const color = normalizeColor(project ? project.color : "");
                    const left = columnLeft(grid, block.start);
                    const width = columnsWidth(grid, block.start, block.length);

                    return (
                        <div
                            key={`${absence ? absence.type : block.orderId}_${block.start}`}
                            className={classnames(styles.block, {
                                // у прошлых блоков свой класс: перетаскивание для них
                                // не регистрируется, а растягивание вправо — да
                                [BLOCK_CLASS]: !block.isPast,
                                [PAST_BLOCK_CLASS]: block.isPast,
                                [styles.blockPast]: block.isPast,
                                [styles.blockTight]: !absence && width < LABEL_TIGHT_WIDTH,
                                [styles.blockDark]: !absence && isDarkBlock(color),
                                [ABSENCE_CLASS[absence ? absenceKind(absence.type) : "other"]]: !!absence,
                            })}
                            data-row={rowIndex}
                            data-start={block.start}
                            data-length={block.length}
                            style={
                                absence
                                    ? { left, width }
                                    : {
                                          left,
                                          width,
                                          backgroundColor: hex2rgba(color, BLOCK_ALPHA),
                                          borderColor: shadeColor(color, BORDER_SHADE),
                                      }
                            }
                            data-tip={absence ? absence.type : project ? project.fullInventoryNumber : ""}
                            data-tip-note={absence ? "" : project ? project.name : ""}
                            onClick={() => onBlockClick(rowIndex, block.start, block.length)}
                        >
                            {!block.isPast && (
                                <span className={`${HANDLE_LEFT_CLASS} ${styles.handleLeft}`} />
                            )}

                            {/* у отсутствия подпись в каждом дне: так видно, сколько дней закрыто.
                                По неделям день слишком узкий — там одна подпись на блок,
                                иначе двухнедельный отпуск превращается в «О О О О О О О О О О» */}
                            {absence && grid.scale === "week"
                                ? width >= LABEL_MIN_WIDTH && (
                                      <span className={styles.blockLabel}>{absenceLabel}</span>
                                  )
                                : absence
                                ? Array.from({ length: block.length }, (_, offset) => block.start + offset).map(
                                      (column) => {
                                          const dayWidth = columnsWidth(grid, column, 1);

                                          return (
                                              <span
                                                  key={column}
                                                  className={styles.absenceDay}
                                                  style={{ left: columnLeft(grid, column) - left, width: dayWidth }}
                                              >
                                                  {dayWidth >= ABSENCE_LETTER_MIN_WIDTH ? absenceLabel : ""}
                                              </span>
                                          );
                                      }
                                  )
                                : width >= LABEL_MIN_WIDTH && (
                                      <span className={styles.blockLabel}>
                                          {project ? project.fullInventoryNumber : "—"}
                                      </span>
                                  )}

                            <span className={`${HANDLE_RIGHT_CLASS} ${styles.handleRight}`} />
                        </div>
                    );
                })}

                {dropPreview && (
                    <div
                        className={classnames(styles.dropGhost, {
                            [styles.overwrites]: overwrites,
                        })}
                        style={{
                            left: columnLeft(grid, dropPreview.start),
                            width: columnsWidth(grid, dropPreview.start, dropPreview.length),
                        }}
                    />
                )}

                {selection && selection.rowIndex === rowIndex && (
                    <div
                        className={classnames(styles.selection, {
                            [SELECTION_CLASS]: !selectionIsBlock,
                            [styles.selectionPassive]: selectionIsBlock,
                        })}
                        data-row={rowIndex}
                        data-start={selection.start}
                        data-length={selection.length}
                        style={{
                            left: columnLeft(grid, selection.start),
                            width: columnsWidth(grid, selection.start, selection.length),
                        }}
                    />
                )}
            </div>
        </div>
    );
});

PlanRowView.displayName = "PlanRowView";
